import { Link } from '@tanstack/react-router'
import { motion } from 'motion/react'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '../../ui'

const fadeInUp = { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } }
const fadeIn = { initial: { opacity: 0 }, animate: { opacity: 1 } }
const scaleIn = { initial: { scale: 0.8, opacity: 0 }, animate: { scale: 1, opacity: 1 } }

export function BackArrowIcon() {
	return (
		<svg
			xmlns="http://www.w3.org/2000/svg"
			fill="none"
			viewBox="0 0 24 24"
			strokeWidth={2}
			stroke="currentColor"
			className="w-5 h-5"
			aria-hidden="true"
		>
			<path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
		</svg>
	)
}

export type BaseErrorAction =
	| {
		label: string
		icon?: ReactNode
		ariaLabel?: string
		to: string
		onClick?: never
	}
	| {
		label: string
		icon?: ReactNode
		ariaLabel?: string
		onClick: () => void | Promise<void>
		to?: never
	}

export type BaseErrorProps = {
	errorCode: string | number
	heading: string
	message: string | ReactNode
	suggestions: string[]
	actions: BaseErrorAction[]
}

export function BaseError({
	errorCode,
	heading,
	message,
	suggestions,
	actions,
}: BaseErrorProps) {
	const { t } = useTranslation('common')
	return (
		<main className="min-h-screen bg-purple-50 flex items-center justify-center p-4">
			<div className="max-w-2xl w-full">
				<motion.article
					{...fadeInUp}
					transition={{ duration: 0.5 }}
					className="bg-white rounded-2xl shadow-lg p-8 md:p-12 text-center"
					role="alert"
					aria-live="polite"
				>
					{/* Big error code */}
					<motion.div
						{...scaleIn}
						transition={{ duration: 0.5, delay: 0.2 }}
						className="text-9xl font-light text-purple-800 mb-4"
						aria-hidden="true"
					>
						{errorCode}
					</motion.div>

					{/* Heading */}
					<motion.h1
						{...fadeIn}
						transition={{ duration: 0.5, delay: 0.4 }}
						className="text-3xl font-light text-grey-700 mb-4"
					>
						{heading}
					</motion.h1>

					{/* Message */}
					<motion.p
						{...fadeIn}
						transition={{ duration: 0.5, delay: 0.6 }}
						className="text-grey-700 mb-8 text-lg"
					>
						{message}
					</motion.p>

					{/* Helpful suggestions */}
					<motion.div
						{...fadeIn}
						transition={{ duration: 0.5, delay: 0.8 }}
						className="bg-purple-50 rounded-lg p-6 mb-8 text-left"
					>
						<h2 className="text-lg font-medium text-grey-700 mb-3">{t('errors.base.whatYouCanDo')}</h2>
						<ul className="space-y-2 text-grey-700" role="list">
							{suggestions.map((suggestion, index) => (
								<li key={index} className="flex items-start">
									<span className="text-purple-800 mr-2" aria-hidden="true">•</span>
									<span>{suggestion}</span>
								</li>
							))}
						</ul>
					</motion.div>

					{/* Action buttons */}
					<motion.div
						{...fadeInUp}
						transition={{ duration: 0.5, delay: 1 }}
						className="flex flex-col sm:flex-row gap-4 justify-center"
					>
						{actions?.map((action, i) =>
							action.to
								? (
									<Link
										key={i}
										to={action.to}
										className="inline-flex items-center gap-2 bg-purple-800 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors font-medium"
										aria-label={action.ariaLabel || action.label}
									>
										{action.icon}
										{action.label}
									</Link>
								)
								: (
									<Button
										key={i}
										onClick={action.onClick}
										aria-label={action.ariaLabel || action.label}
									>
										{action.icon}
										{action.label}
									</Button>
								)
						)}
					</motion.div>
				</motion.article>

				{/* Footer message */}
				<motion.p
					{...fadeIn}
					transition={{ duration: 0.5, delay: 1.2 }}
					className="text-center text-grey-700 mt-6 text-sm"
				>
					{t('errors.base.needHelp')}
				</motion.p>
			</div>
		</main>
	)
}
